import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import PropertyCard from '../components/PropertyCard';
import { useLang } from '../context/LanguageContext';
import { fetchProperties } from '../lib/api';
import ContactForm from '../components/ContactForm';

export default function Home() {
  const { t } = useLang();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchProperties()
      .then((data) => {
        const filtered = data.filter((p) => {
          const price = (p.price || '').trim().toLowerCase();
          const isRented = price === 'rented' || price === 'loué' || price === 'verhuurd';
          const isSold = price === 'sold' || price === 'vendu' || price === 'verkocht';
          // Keep the sold penthouse (Beau Rivage) as a showcase; hide all other rented/sold
          if (p.slug === 'beau-rivage') return true;
          return !isRented && !isSold;
        });
        // Guarantee beau-rivage appears in the 6-card grid
        const beau = filtered.find((p) => p.slug === 'beau-rivage');
        const others = filtered.filter((p) => p.slug !== 'beau-rivage').slice(0, 5);
        setProjects(beau ? [...others, beau] : filtered.slice(0, 6));
      })
      .catch(() => setProjects([]));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-5 gap-0">
          <div className="lg:col-span-2 order-2 lg:order-1 px-6 md:px-12 lg:px-16 py-14 lg:py-32 flex flex-col justify-center bg-white">
            <SectionLabel>{t.hero.brand}</SectionLabel>
            <h1 className="mt-8 text-4xl md:text-5xl lg:text-[56px] font-light leading-[1.05] tracking-tight text-black">
              {t.hero.title}
            </h1>
            <div className="mt-10 flex items-center gap-4">
              <div className="h-px w-10 bg-[#e11f3a]" />
              <p className="text-[13px] tracking-[0.4em] uppercase text-black font-medium">
                {t.hero.tagline}
              </p>
            </div>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/new-projects"
                className="group inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-sm tracking-[0.15em] hover:bg-[#e11f3a] transition-colors"
              >
                {t.hero.cta_projects}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-black px-6 py-3 text-sm tracking-[0.15em] hover:bg-black hover:text-white transition-colors"
              >
                {t.hero.cta_contact}
              </Link>
            </div>
          </div>
          <div className="relative lg:col-span-3 order-1 lg:order-2 min-h-[440px] sm:min-h-[520px] lg:min-h-[880px] bg-neutral-100">
            <img
              src="/assets/whoweare/exterior.webp"
              alt="Featured project"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-6 right-6 lg:bottom-8 lg:right-8 bg-white/95 backdrop-blur px-6 py-5 lg:px-7 lg:py-6 max-w-[280px] lg:max-w-[300px] shadow-lg">
              <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">{t.hero.featured}</p>
              <p className="text-[13px] tracking-[0.22em] uppercase font-normal mt-2">La Réserve</p>
              <a
                href="https://lareserve-brussels.be/?lang=en"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] tracking-[0.25em] uppercase text-[#e11f3a] mt-3 hover:gap-2 transition-all"
              >
                {t.sections.view_project}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionLabel>{t.sections.who}</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-light tracking-tight leading-tight">
              {t.sections.who_headline}
            </h2>
            <p className="mt-6 text-[15px] md:text-base leading-relaxed text-neutral-700 max-w-xl">
              {t.sections.who_text}
            </p>
            <Link
              to="/who-we-are"
              className="mt-10 inline-flex items-center gap-2 border border-black px-6 py-3 text-sm tracking-[0.15em] hover:bg-black hover:text-white transition-colors"
            >
              {t.sections.view_more}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src="/assets/whoweare/interior1.webp"
              alt="Who we are"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* OUR PORTFOLIO */}
      <section className="bg-neutral-50 px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <SectionLabel>{t.sections.portfolio_label}</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl font-light tracking-tight leading-tight">
                {t.sections.latest}
              </h2>
            </div>
            <Link
              to="/new-projects"
              className="inline-flex items-center gap-2 text-sm text-black hover:text-[#e11f3a] transition-colors"
            >
              {t.sections.browse_all}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((p) => (
              <PropertyCard key={p.slug} property={p} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* MANAGEMENT */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="bg-black text-white px-8 md:px-16 py-16 lg:py-24 flex flex-col justify-center">
            <SectionLabel className="[&_h4]:text-white">
              <span className="text-white">{t.management.hero_label}</span>
            </SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-light leading-tight">
              {t.management.hero_title}
            </h2>
            <p className="mt-6 text-neutral-300 leading-relaxed max-w-lg">
              {t.management.hero_text}
            </p>
            <Link
              to="/management"
              className="mt-8 inline-flex items-center gap-2 border border-white/40 px-6 py-3 text-sm tracking-[0.15em] hover:bg-white hover:text-black transition self-start"
            >
              {t.sections.view_more}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative min-h-[360px] lg:min-h-[560px]">
            <img
              src="/assets/whoweare/living.webp"
              alt="Property management"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-white px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1000px] mx-auto text-center">
          <div className="flex justify-center">
            <span className="inline-block w-[3px] h-4 bg-[#e11f3a]" />
          </div>
          <p className="mt-4 text-[13px] tracking-[0.3em] text-neutral-500">
            {t.sections.mission}
          </p>
          <h3 className="mt-8 text-2xl md:text-3xl lg:text-4xl font-light leading-relaxed">
            {t.sections.mission_text}
          </h3>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-neutral-50 px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-16">
          <div>
            <SectionLabel>{t.sections.contact_us}</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-light tracking-tight leading-tight">
              {t.nav.contact}
            </h2>
            <p className="mt-6 text-[15px] text-neutral-700 max-w-md">
              {t.sections.contact_intro}
            </p>
            <div className="mt-10 space-y-4 text-sm">
              <div>
                <span className="text-neutral-500 tracking-[0.2em] text-xs">{t.sections.email_label}</span>
                <p className="mt-1 text-black">gestion@imargo.be</p>
              </div>
              <div>
                <span className="text-neutral-500 tracking-[0.2em] text-xs">{t.sections.phone_label}</span>
                <p className="mt-1 text-black">02 644 10 20</p>
              </div>
              <div>
                <span className="text-neutral-500 tracking-[0.2em] text-xs">{t.sections.address_label}</span>
                <p className="mt-1 text-black">Avenue Louise 335, 1050 Bruxelles</p>
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>
    </div>
  );
}
